/**
 * StatusPublicPage.tsx — /s/:slug — public per-tenant status page
 *
 * Purpose:    Unauthenticated status page shown to end users of a tenant's
 *             monitored services. No app chrome.
 * Inputs:     :slug route param; statusPagesApi.public from @/lib/saas-client
 * Outputs:    Title, overall banner, component list, recent incidents
 * Constraints: No auth; dark-first styling; WCAG AA
 * SOT:        nsentry SaaS app zone
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AsyncScreen, type AsyncState } from '@/components/AsyncScreen'
import { statusPagesApi, type PublicStatusPage } from '@/lib/saas-client'
import type { SentryError } from '@/lib/sentry-error'

const OVERALL_STYLES: Record<string, { label: string; className: string }> = {
  operational: { label: 'All systems operational', className: 'border-green-200 dark:border-green-800/60 bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300' },
  degraded: { label: 'Degraded performance', className: 'border-yellow-200 dark:border-yellow-800/60 bg-yellow-50 dark:bg-yellow-950/40 text-yellow-700 dark:text-yellow-300' },
  down: { label: 'Service disruption', className: 'border-red-200 dark:border-red-800/60 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300' },
}

const DOT_STYLES: Record<string, string> = {
  operational: 'bg-green-500',
  degraded: 'bg-yellow-500',
  down: 'bg-red-500',
  unknown: 'bg-gray-500',
}

export function StatusPublicPage() {
  const { slug } = useParams<{ slug: string }>()
  const [state, setState] = useState<AsyncState>('loading')
  const [page, setPage] = useState<PublicStatusPage | null>(null)
  const [error, setError] = useState<SentryError | undefined>()

  async function load() {
    if (!slug) return
    setState('loading')
    const result = await statusPagesApi.public(slug)
    if (!result.ok) {
      setError(result.error)
      setState('error')
      return
    }
    setPage(result.value)
    setState('success')
  }

  useEffect(() => {
    void load()
  }, [slug])

  const overall = page ? (OVERALL_STYLES[page.overallStatus] ?? OVERALL_STYLES.operational) : OVERALL_STYLES.operational

  return (
    <div className="min-h-screen bg-brand-bg px-4 py-12">
      <div className="mx-auto max-w-2xl">
        <AsyncScreen state={state} error={error} onRetry={load} emptyMessage="Status page not found">
          {page && (
            <>
              <h1 className="mb-6 text-2xl font-bold text-brand-ink">{page.title}</h1>

              <div className={`mb-8 rounded-xl border px-5 py-4 text-sm font-medium ${overall.className}`}>
                {overall.label}
              </div>

              <section aria-label="Components" className="mb-8">
                {page.components.length === 0 ? (
                  <p className="rounded-lg border border-brand-subtle bg-brand-surface px-4 py-6 text-center text-sm text-brand-muted">
                    No public components yet.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {page.components.map((c) => (
                      <li key={c.id} className="flex items-center justify-between rounded-lg border border-brand-subtle bg-brand-surface px-4 py-3">
                        <span className="text-gray-200">{c.name}</span>
                        <span className="flex items-center gap-3 text-xs text-brand-muted">
                          {c.uptimePercent != null && (
                            <span className="tabular-nums text-brand-muted">{c.uptimePercent.toFixed(2)}% uptime</span>
                          )}
                          <span className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${DOT_STYLES[c.status] ?? DOT_STYLES.unknown}`} aria-hidden="true" />
                            {c.status}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {page.incidents.length > 0 && (
                <section aria-label="Recent incidents" className="mb-8">
                  <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-muted">Recent incidents</h2>
                  <ul className="space-y-2">
                    {page.incidents.map((i, idx) => (
                      <li key={idx} className="rounded-lg border border-brand-subtle bg-brand-surface px-4 py-3 text-sm">
                        <p className="text-gray-200">{i.title}</p>
                        <p className="mt-0.5 text-xs text-brand-muted">{i.status} — {new Date(i.startedAt).toLocaleString()}</p>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </AsyncScreen>

        <footer className="mt-12 text-center text-xs text-brand-muted">
          Powered by ɳSentry — <a href="https://sentry.nself.org" className="hover:text-brand-muted">sentry.nself.org</a>
        </footer>
      </div>
    </div>
  )
}
